import { useState, useEffect, createContext, useContext } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { localStoreAPI, localSyncAPI } from '@/services/localApi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Monitor, Wifi, WifiOff, RefreshCw, LogOut, Settings,
  ShoppingCart, DollarSign, BarChart3, Clock, Package
} from 'lucide-react';

// Context for terminal selection
const TerminalContext = createContext(null);

export const useTerminal = () => {
  const context = useContext(TerminalContext);
  if (!context) {
    throw new Error('useTerminal must be used within LocalStoreLayout');
  }
  return context;
};

const LocalStoreLayout = () => {
  const navigate = useNavigate();
  const [storeInfo, setStoreInfo] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStoreData();
    // Poll sync status every 30 seconds
    const interval = setInterval(checkSyncStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStoreData = async () => {
    try {
      setLoading(true);
      const [storeRes, terminalsRes, statusRes] = await Promise.all([
        localStoreAPI.getStoreInfo(),
        localSyncAPI.getTerminals(),
        localSyncAPI.getStatus()
      ]);

      if (!storeRes.data.configured) {
        navigate('/local-store/setup');
        return;
      }

      setStoreInfo(storeRes.data);
      setTerminals(terminalsRes.data.terminals || []);
      setSyncStatus(statusRes.data);
      setIsOnline(statusRes.data.is_online);

      // Auto-select first terminal if none selected
      const savedTerminal = localStorage.getItem('selectedTerminal');
      if (savedTerminal && terminalsRes.data.terminals?.find(t => t.terminal_id === savedTerminal)) {
        setSelectedTerminal(savedTerminal);
      }
    } catch (error) {
      console.error('Failed to load store data:', error);
      toast.error('Cannot connect to local server');
    } finally {
      setLoading(false);
    }
  };

  const checkSyncStatus = async () => {
    try {
      const statusRes = await localSyncAPI.getStatus();
      setSyncStatus(statusRes.data);
      setIsOnline(statusRes.data.is_online);
    } catch (error) {
      setIsOnline(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      // First push local data
      const pushRes = await localSyncAPI.pushToHQ();
      if (pushRes.data.result?.sales > 0 || pushRes.data.result?.registers > 0) {
        toast.success(`Synced: ${pushRes.data.result.sales} sales, ${pushRes.data.result.registers} registers`);
      }

      // Then pull updates
      const pullRes = await localSyncAPI.pullFromHQ();
      if (pullRes.data.result?.products > 0) {
        toast.success(`Updated: ${pullRes.data.result.products} products`);
      }

      await checkSyncStatus();
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSelectTerminal = (terminalId) => {
    setSelectedTerminal(terminalId);
    localStorage.setItem('selectedTerminal', terminalId);
    navigate('/local-store/pos');
  };

  const handleExitTerminal = () => {
    setSelectedTerminal(null);
    localStorage.removeItem('selectedTerminal');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Connecting to Local Server...</div>
      </div>
    );
  }

  // Terminal Selection Screen
  if (!selectedTerminal) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">{storeInfo?.store_name}</h1>
            <p className="text-gray-400">Store Code: {storeInfo?.store_code}</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Online Status Indicator */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isOnline ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              {isOnline ? (
                <>
                  <Wifi className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 font-medium">Online</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-5 h-5 text-red-400" />
                  <span className="text-red-400 font-medium">Offline</span>
                </>
              )}
            </div>
            
            {/* Sync Button */}
            <Button
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              onClick={handleSync}
              disabled={syncing || !isOnline}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>

            {/* Pending Badge */}
            {syncStatus?.total_pending > 0 && (
              <Badge variant="destructive" className="text-sm">
                {syncStatus.total_pending} Pending
              </Badge>
            )}
          </div>
        </div>

        {/* Terminal Grid */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Select Terminal</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {terminals.map((terminal) => (
              <button
                key={terminal.terminal_id}
                onClick={() => handleSelectTerminal(terminal.terminal_id)}
                className="bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-blue-500 rounded-xl p-6 transition-all group"
                data-testid={`terminal-${terminal.terminal_id}`}
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                    <Monitor className="w-8 h-8 text-blue-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-white font-bold text-lg">{terminal.terminal_id}</p>
                    <p className="text-gray-400 text-sm">{terminal.terminal_name}</p>
                  </div>
                  {terminal.current_register_id && (
                    <Badge variant="secondary" className="bg-green-500/20 text-green-400">
                      Register Open
                    </Badge>
                  )}
                </div>
              </button>
            ))}

            {/* Add Terminal Button (if less than 5) */}
            {terminals.length < 5 && (
              <button
                onClick={() => navigate('/local-store/settings')}
                className="bg-gray-800/50 hover:bg-gray-800 border border-dashed border-gray-600 rounded-xl p-6 transition-all"
              >
                <div className="flex flex-col items-center gap-3 text-gray-500">
                  <div className="w-16 h-16 rounded-full border-2 border-dashed border-gray-600 flex items-center justify-center">
                    <span className="text-3xl">+</span>
                  </div>
                  <p className="text-sm">Add Terminal</p>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Products</p>
                <p className="text-white text-xl font-bold">{storeInfo?.products_count || '-'}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Pending Sales</p>
                <p className="text-white text-xl font-bold">{syncStatus?.pending_sales || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Last Sync</p>
                <p className="text-white text-sm font-medium">
                  {syncStatus?.last_sync_at 
                    ? new Date(syncStatus.last_sync_at).toLocaleTimeString()
                    : 'Never'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Terminals</p>
                <p className="text-white text-xl font-bold">{terminals.length}/5</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Layout with Selected Terminal
  const currentTerminal = terminals.find(t => t.terminal_id === selectedTerminal);

  return (
    <TerminalContext.Provider value={{ 
      terminalId: selectedTerminal, 
      terminal: currentTerminal,
      storeInfo,
      isOnline,
      syncStatus,
      refreshStatus: checkSyncStatus
    }}>
      <div className="min-h-screen bg-gray-100 flex flex-col">
        {/* Top Bar */}
        <header className="bg-gray-900 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Monitor className="w-5 h-5 text-blue-400" />
              <span className="font-bold">{selectedTerminal}</span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-300">{currentTerminal?.terminal_name}</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Online Status */}
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${isOnline ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              {isOnline ? (
                <>
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-green-400">Online</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="text-red-400">Offline</span>
                </>
              )}
            </div>

            {/* Pending Sync */}
            {syncStatus?.total_pending > 0 && (
              <Badge variant="destructive" className="text-xs">
                {syncStatus.total_pending} pending
              </Badge>
            )}

            {/* Sync Button */}
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:text-white hover:bg-gray-800"
              onClick={handleSync}
              disabled={syncing}
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            </Button>

            {/* Exit Terminal */}
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:text-white hover:bg-gray-800"
              onClick={handleExitTerminal}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>

        {/* Navigation */}
        <nav className="bg-white border-b px-4 py-2 flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/local-store/pos')}
            className="gap-2"
          >
            <ShoppingCart className="w-4 h-4" />
            POS Billing
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/local-store/register')}
            className="gap-2"
          >
            <DollarSign className="w-4 h-4" />
            Cash Register
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/local-store/sales')}
            className="gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            Sales History
          </Button>
        </nav>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </TerminalContext.Provider>
  );
};

export default LocalStoreLayout;
